(() => {
  if (customElements.get('bl-section')) return;
  class BloomSection extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.classList.add("bl-initialized");
      this.abort = new AbortController();
      this.signal = this.abort.signal;
      this.kind = this.dataset.kind;
      this.slides = [...this.querySelectorAll('[data-slide]')];
      this.tiles = [...this.querySelectorAll('[data-tile]')];
      this.scrollSlides = [...this.querySelectorAll('[data-scroll-slide]')];
      this.track = this.querySelector('[data-scroll-track]');
      this.count = this.kind === 'mosaic' ? this.tiles.length : this.track ? this.scrollSlides.length : this.slides.length;
      this.index = 0;
      this.auto = this.dataset.autoplay === 'true';
      this.paused = false;
      this.visible = false;
      this.editorSelection = false;
      this.reduced = matchMedia('(prefers-reduced-motion: reduce)');
      this.typed = this.querySelector('[data-type-text]');
      this.typeStrings = [...this.querySelectorAll('[data-type-strings] span')].map(x => x.textContent);
      const listen = (target, event, fn) => target.addEventListener(event, fn, { signal: this.signal });
      listen(this, 'click', e => {
        if (e.target.closest('[data-prev]')) this.change(this.index - 1);
        if (e.target.closest('[data-next]')) this.change(this.index + 1);
        if (e.target.closest('[data-toggle]')) {
          this.paused = !this.paused;
          const b = this.querySelector('[data-toggle]');
          b.textContent = this.paused ? 'Play' : 'Pause';
          b.setAttribute('aria-label', this.paused ? 'Play automatic slides' : 'Pause automatic slides');
          b.setAttribute('aria-pressed', String(this.paused));
          this.schedule();
        }
      });
      listen(this, 'mouseenter', () => { this.hovered = true; this.stop(); });
      listen(this, 'mouseleave', () => { this.hovered = false; this.schedule(); });
      listen(this, 'focusin', () => this.stop());
      listen(this, 'focusout', () => requestAnimationFrame(() => this.schedule()));
      listen(document, 'visibilitychange', () => this.schedule());
      listen(this.reduced, 'change', () => { this.clearType(); this.change(this.index, true); });
      listen(document, 'shopify:block:select', e => {
        const block = this.querySelector(`[data-shopify-editor-block*='"id":"${e.detail.blockId}"']`);
        if (!block) return;
        this.editorSelection = true;
        this.stop();
        const list = this.kind === 'mosaic' ? this.tiles : this.track ? this.scrollSlides : this.slides;
        const i = list.indexOf(block);
        if (i >= 0) this.change(i);
      });
      listen(document, 'shopify:block:deselect', e => {
        if (this.querySelector(`[data-shopify-editor-block*='"id":"${e.detail.blockId}"']`)) {
          this.editorSelection = false; this.schedule();
        }
      });
      this.observer = new IntersectionObserver(entries => {
        this.visible = entries[0].isIntersecting;
        if (this.visible) this.classList.add('is-visible');
        this.schedule();
      }, { threshold: .08 });
      this.observer.observe(this);
      this.querySelectorAll('.bl-slides').forEach(el => el.classList.add('is-initialized'));
      this.change(0, true);
      if (this.track) {
        listen(this.track, 'scroll', () => {
          clearTimeout(this.scrollTimer);
          this.scrollTimer = setTimeout(() => {
            const center = this.track.getBoundingClientRect().left + this.track.clientWidth / 2;
            let distance = Infinity;
            this.scrollSlides.forEach((slide, i) => {
              const rect = slide.getBoundingClientRect();
              const d = Math.abs(rect.left + rect.width / 2 - center);
              if (d < distance) { distance = d; this.index = i; }
            });
            this.counter();
          }, 160);
        });
      }
      const bg = this.querySelector('.bl-testimonial-bg img');
      if (bg && this.dataset.fixed === 'true') {
        const apply = () => bg.parentElement.style.backgroundImage = `url("${bg.currentSrc || bg.src}")`;
        if (bg.complete) apply(); else listen(bg, 'load', apply);
      }
    }
    clearType() { clearTimeout(this.typeTimer); }
    counter() {
      const label = this.querySelector('[data-counter]');
      if (label) label.textContent = `${this.index + 1} / ${this.count}`;
    }
    change(i, instant = false) {
      if (!this.count) return;
      this.index = (i + this.count) % this.count;
      this.slides.forEach((slide, index) => {
        const active = index === this.index;
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-hidden', String(!active));
        slide.inert = !active;
      });
      if (this.kind === 'mosaic') {
        if (!instant && !this.reduced.matches) this.querySelector('[data-gallery]')?.animate([{opacity:.25},{opacity:1}],{duration:700,easing:'ease-out'});
        this.tiles.forEach(tile => { tile.hidden = true; });
        for (let slot = 0; slot < Math.min(4, this.count); slot++) {
          const tile = this.tiles[(this.index + slot) % this.count];
          tile.hidden = false;
          tile.dataset.slot = slot + 1;
        }
      }
      if (this.track) {
        const target = this.scrollSlides[this.index];
        const left = target.offsetLeft - this.track.offsetLeft - (this.track.clientWidth - target.clientWidth) / 2;
        this.track.scrollTo({ left: Math.max(0, left), behavior: instant || this.reduced.matches ? 'instant' : 'smooth' });
      }
      if (this.typed) {
        this.clearType();
        const text = this.typeStrings[this.index] || '';
        this.querySelector('[data-type-accessible]').textContent = text;
        if (this.reduced.matches || instant) this.typed.textContent = text;
        else {
          this.typed.textContent = '';
          const chars = Array.from(text);
          let n = 0;
          const tick = () => {
            this.typed.textContent = chars.slice(0, ++n).join('');
            if (n < chars.length) this.typeTimer = setTimeout(tick, Number(this.dataset.typeSpeed) || 45);
          };
          tick();
        }
      }
      this.counter();
      this.schedule();
    }
    stop() { clearTimeout(this.timer); }
    schedule() {
      this.stop();
      if (!this.auto || this.count < 2 || this.paused || !this.visible || document.hidden || this.hovered || this.contains(document.activeElement) || this.editorSelection || this.reduced.matches) return;
      let delay = Number(this.dataset.interval) || 6000;
      if (this.typed) delay = Math.max(delay, (this.typeStrings[this.index] || '').length * (Number(this.dataset.typeSpeed) || 45) + 1600);
      this.timer = setTimeout(() => this.change(this.index + 1), delay);
    }
    disconnectedCallback() {
      this.stop(); this.clearType(); clearTimeout(this.scrollTimer);
      this.observer?.disconnect(); this.abort?.abort(); this.ready = false;
    }
  }
  customElements.define('bl-section', BloomSection);
})();
